import {
  ArrowLeftOutlined,
  CodeOutlined,
  EditOutlined,
  NotificationOutlined,
  PoweroffOutlined,
  UserAddOutlined,
  VideoCameraAddOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AddSpeakerToEventModal } from '../components/AddSpeakerToEventModal';
import { AppLayout } from '../components/AppLayout';
import { EditEventModal } from '../components/EditEventModal';
import { PageHead } from '../components/PageHead';
import {
  SendMessageModal,
  broadcastMessageToEvent,
} from '../components/SendMessageModal';
import {
  SlidesMonitor,
  type MonitoredLicense,
} from '../components/SlidesMonitor';
import { SlotCard } from '../components/SlotCard';
import { SlotCardEmpty } from '../components/SlotCardEmpty';
import { WaitingRoomList } from '../components/WaitingRoomList';
import { api, type EventDetail } from '../lib/api';
import { useAuth } from '../lib/auth';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';

const { Title, Text } = Typography;

export type PresenceInfo = {
  status: 'online' | 'hidden' | 'offline';
  platform?: string;
  location?: string;
  lastSeenAt?: number;
};

type RoomMessage =
  | ({ type: 'presence'; speakerId: string } & PresenceInfo)
  | { type: 'assign'; speakerId: string; vmixId: string }
  | { type: 'unassign'; speakerId: string }
  | { type: 'hand'; speakerId: string; raised: boolean }
  | { type: 'note'; eventSpeakerId: string; note: string | null };

export function EventScene() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [addSpeakerOpen, setAddSpeakerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [embedDraft, setEmbedDraft] = useState('');
  const [embedSaving, setEmbedSaving] = useState(false);
  const [presence, setPresence] = useState<Record<string, PresenceInfo>>({});

  const load = async () => {
    if (!id) return;
    try {
      const detail = await api<EventDetail>(`/api/events/${id}`);
      setEvent(detail);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [id]);

  const presenceWsUrl = useMemo(() => {
    if (!id || !session) return null;
    return wsUrl(`/api/event/${id}/waiting?role=team&token=${encodeURIComponent(session.token)}`);
  }, [id, session]);

  useWebSocket<RoomMessage>(presenceWsUrl, (msg) => {
    if (msg.type === 'presence') {
      const { type: _ignored, speakerId, ...rest } = msg;
      setPresence((p) => ({ ...p, [speakerId]: rest }));
    } else if (msg.type === 'hand' || msg.type === 'note') {
      // Hand-raise and note changes mutate event_speaker rows — refetch.
      void load();
    }
  });

  if (loading || !event) {
    return (
      <AppLayout>
        <PageHead title="Event" />
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin />
        </div>
      </AppLayout>
    );
  }

  const ended = event.status === 'ended';

  const liveSpeakerIds = new Set(
    event.slots.filter((s) => s.speaker_id).map((s) => s.speaker_id),
  );
  const waitingRoomSpeakers = event.speakers.filter(
    (es) => !liveSpeakerIds.has(es.speaker_id),
  );

  // Distinct slide licenses used by this event's speakers. We only show one
  // monitor row per license, even if several speakers share it.
  const monitoredLicenses: MonitoredLicense[] = (() => {
    const seen = new Map<string, MonitoredLicense>();
    for (const es of event.speakers) {
      if (!es.slide_license_id || !es.slide_license_key || !es.slide_license_name) {
        continue;
      }
      const existing = seen.get(es.slide_license_id);
      const label = es.name ?? es.email ?? '';
      if (existing) {
        existing.speakerLabel = existing.speakerLabel.includes(label)
          ? existing.speakerLabel
          : `${existing.speakerLabel}, ${label}`;
      } else {
        seen.set(es.slide_license_id, {
          licenseId: es.slide_license_id,
          licenseName: es.slide_license_name,
          licenseKey: es.slide_license_key,
          speakerLabel: label,
        });
      }
    }
    return Array.from(seen.values());
  })();

  const sendInvite = async (speakerId: string) => {
    try {
      const res = await api<{ ok: boolean; link?: string }>('/api/invite', {
        method: 'POST',
        body: JSON.stringify({ event_id: id, speaker_id: speakerId }),
      });
      if (res.link) {
        Modal.info({
          title: 'Invite link (dev mode)',
          content: <a href={res.link}>{res.link}</a>,
        });
      } else {
        message.success('Invite sent');
      }
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to send invite');
    }
  };

  const addSlot = async () => {
    try {
      await api(`/api/events/${id}/slots`, { method: 'POST' });
      await load();
      message.success(`Added slot ${event.slots.length + 1}`);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to add slot');
    }
  };

  const endEvent = async () => {
    try {
      await api(`/api/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'ended' }),
      });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to end event');
    }
  };

  const reopenEvent = async () => {
    try {
      await api(`/api/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'draft' }),
      });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to reopen');
    }
  };

  const everythingEmpty =
    waitingRoomSpeakers.length === 0 && event.slots.length === 0;

  return (
    <AppLayout>
      <PageHead title={event.title} />

      <Space
        align="start"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }}
      >
        <Space align="center" size="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/events')}
          />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {event.title}
            </Title>
            {event.date && (
              <Text type="secondary">{dayjs(event.date).format('dddd, D MMMM YYYY')}</Text>
            )}
          </div>
        </Space>

        <Space wrap>
          {!ended && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
              <Button
                icon={<UserAddOutlined />}
                onClick={() => setAddSpeakerOpen(true)}
              >
                Add speaker
              </Button>
              <Button
                icon={<CodeOutlined />}
                onClick={() => {
                  setEmbedDraft(event.embed_code ?? '');
                  setEmbedOpen(true);
                }}
              >
                {event.embed_code ? 'Edit livestream' : 'Add livestream'}
              </Button>
              <Button
                icon={<NotificationOutlined />}
                onClick={() => setBroadcastOpen(true)}
              >
                Broadcast
              </Button>
              <Button
                type="primary"
                icon={<VideoCameraAddOutlined />}
                onClick={addSlot}
              >
                Add call slot
              </Button>
              <Popconfirm
                title="End this event?"
                description="This will stop the event for all speakers."
                okText="End event"
                okButtonProps={{ danger: true }}
                onConfirm={endEvent}
              >
                <Button danger icon={<PoweroffOutlined />}>
                  End event
                </Button>
              </Popconfirm>
            </>
          )}
          {ended && (
            <Button type="primary" onClick={reopenEvent}>
              Re-open event
            </Button>
          )}
        </Space>
      </Space>

      {!ended && monitoredLicenses.length > 0 && (
        <SlidesMonitor licenses={monitoredLicenses} />
      )}

      {ended ? (
        <Card>
          <Empty
            description={
              <>
                <Title level={4}>Event ended</Title>
                <Text type="secondary">
                  Re-open the event to bring speakers back online.
                </Text>
              </>
            }
          />
        </Card>
      ) : everythingEmpty ? (
        <Card>
          <Empty
            description="No speakers or slots yet — add a speaker and a slot to get started."
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} lg={10} xl={8} xxl={7}>
            <Card bodyStyle={{ padding: 0 }}>
              <WaitingRoomList
                speakers={waitingRoomSpeakers}
                slots={event.slots}
                presence={presence}
                onChange={load}
                onSendInvite={sendInvite}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={14} xl={16} xxl={17}>
            {event.slots.length === 0 ? (
              <Card>
                <Empty description="No slots yet — add a call slot to start filling them." />
              </Card>
            ) : (
              <Row gutter={[16, 16]}>
                {event.slots.map((slot) => {
                  const assignedSpeaker = event.speakers.find(
                    (es) => es.speaker_id === slot.speaker_id,
                  );

                  if (slot.speaker_id && assignedSpeaker) {
                    return (
                      <Col key={slot.id} xs={24} sm={12} md={24} lg={12} xl={8} xxl={6}>
                        <SlotCard
                          slot={slot}
                          speaker={assignedSpeaker}
                          presence={presence[slot.speaker_id] ?? { status: 'offline' }}
                          onVMixChange={async (vmix) => {
                            await api(`/api/slots/${slot.id}`, {
                              method: 'PATCH',
                              body: JSON.stringify({ vmix_id: vmix || null }),
                            });
                            await load();
                          }}
                          onSlideControlChange={load}
                          onRoleChange={async (role) => {
                            await api(
                              `/api/events/${id}/speakers/${assignedSpeaker.id}`,
                              {
                                method: 'PATCH',
                                body: JSON.stringify({ role }),
                              },
                            );
                            await load();
                          }}
                          onTakeOffAir={async () => {
                            await api(`/api/slots/${slot.id}/assign`, {
                              method: 'DELETE',
                            });
                            await load();
                          }}
                          onDelete={async () => {
                            await api(`/api/slots/${slot.id}`, { method: 'DELETE' });
                            await load();
                          }}
                          onRemoveSpeakerFromEvent={async () => {
                            await api(
                              `/api/events/${id}/speakers/${assignedSpeaker.id}`,
                              { method: 'DELETE' },
                            );
                            await load();
                          }}
                        />
                      </Col>
                    );
                  }

                  return (
                    <Col key={slot.id} xs={24} sm={12} md={24} lg={12} xl={8} xxl={6}>
                      <SlotCardEmpty
                        slot={slot}
                        availableSpeakers={waitingRoomSpeakers}
                        presence={presence}
                        onVMixChange={async (vmix) => {
                          await api(`/api/slots/${slot.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ vmix_id: vmix || null }),
                          });
                          await load();
                        }}
                        onSpeakerChange={async (speakerId) => {
                          const isZoom = slot.source_type === 'zoom';
                          if (isZoom && !slot.zoom_meeting_id) {
                            message.warning('Add the Zoom Meeting ID first.');
                            return;
                          }
                          if (!isZoom && !slot.vmix_id) {
                            message.warning('Set the vMix ID first.');
                            return;
                          }
                          await api(`/api/slots/${slot.id}/assign`, {
                            method: 'POST',
                            body: JSON.stringify(
                              isZoom
                                ? { speaker_id: speakerId }
                                : { speaker_id: speakerId, vmix_id: slot.vmix_id },
                            ),
                          });
                          await load();
                        }}
                        onDelete={async () => {
                          await api(`/api/slots/${slot.id}`, { method: 'DELETE' });
                          await load();
                        }}
                        onChanged={load}
                      />
                    </Col>
                  );
                })}
              </Row>
            )}
          </Col>
        </Row>
      )}

      <AddSpeakerToEventModal
        open={addSpeakerOpen}
        eventId={id ?? ''}
        onClose={() => setAddSpeakerOpen(false)}
        onDone={load}
      />

      <EditEventModal
        open={editOpen}
        event={event}
        onClose={() => setEditOpen(false)}
        onDone={load}
      />

      <SendMessageModal
        open={broadcastOpen}
        recipient="everyone on this event"
        onSend={async (payload) => {
          if (id) await broadcastMessageToEvent(id, payload);
        }}
        onClose={() => setBroadcastOpen(false)}
      />

      <Modal
        title="Waiting-room livestream embed"
        open={embedOpen}
        onCancel={() => setEmbedOpen(false)}
        confirmLoading={embedSaving}
        okText="Save"
        onOk={async () => {
          setEmbedSaving(true);
          try {
            await api(`/api/events/${id}`, {
              method: 'PUT',
              body: JSON.stringify({ embed_code: embedDraft || null }),
            });
            setEmbedOpen(false);
            await load();
          } catch (err) {
            message.error((err as { error?: string }).error ?? 'Failed to save');
          } finally {
            setEmbedSaving(false);
          }
        }}
      >
        <p style={{ color: 'var(--text-dim)', marginTop: 0 }}>
          Paste an HTML embed snippet (e.g. a YouTube live iframe). Speakers
          see it above the waiting-room holding message while they wait.
          Leave blank to remove.
        </p>
        <Input.TextArea
          value={embedDraft}
          onChange={(e) => setEmbedDraft(e.target.value)}
          rows={6}
          autoFocus
          placeholder='<iframe src="https://www.youtube.com/embed/..." …></iframe>'
          style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
        />
      </Modal>
    </AppLayout>
  );
}
